import React from 'react';
import { injectIntl } from 'react-intl';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NumberFormat from 'react-number-format';
import { SlideTransition } from '../../../../../components/Transition/Transition';
import { DEFAULT_PREMIUM_FINANCING_PROFILE } from '../../../../../constants/premium-financing';
import { sanitizeLoanInterest } from '../SideLoanInterestInput/SideLoanInterestInput';
import './LoanInterestCalculatorDialog.scss';

/** Input text / underline / "較低" chip — primary blue for both rate rows. */
const INPUT_COLOR = '#3966f8';
/** HIBOR row: xx.xx — auto-decimal after 2 integer digits. */
const HIBOR_MAX_INTEGER_DIGITS = 2;
/** PRIME row: x.xx — auto-decimal after 1 integer digit. */
const PRIME_MAX_INTEGER_DIGITS = 1;

/** Auto-insert `.` after `maxIntegerDigits` so the next keystrokes fill decimals. */
export const autoInsertRateDecimal = (value, decimalScale = 2, maxIntegerDigits = PRIME_MAX_INTEGER_DIGITS) => {
  if (value == null || value === '' || value === '-' || value === '.') return value;
  const str = String(value);
  const neg = str.startsWith('-');
  const body = neg ? str.slice(1) : str;
  if (body === '' || body === '.') return str;

  if (body.includes('.')) {
    const [intPart, fracPart = ''] = body.split('.');
    return `${neg ? '-' : ''}${intPart}.${fracPart.slice(0, decimalScale)}`;
  }

  if (body.length <= maxIntegerDigits) {
    return `${neg ? '-' : ''}${body}`;
  }

  const intPart = body.slice(0, maxIntegerDigits);
  const fracPart = body.slice(maxIntegerDigits, maxIntegerDigits + decimalScale);
  return `${neg ? '-' : ''}${intPart}.${fracPart}`;
};

const RateNumberFormat = React.forwardRef(function RateNumberFormat(props, ref) {
  const { onChange, decimalScale = 2, maxIntegerDigits = PRIME_MAX_INTEGER_DIGITS, ...rest } = props;
  return (
    <NumberFormat
      {...rest}
      getInputRef={ref}
      decimalScale={decimalScale}
      onValueChange={values => {
        const next = autoInsertRateDecimal(values.value, decimalScale, maxIntegerDigits);
        onChange({
          target: {
            name: props.name,
            value: next !== '' && next !== '-' && next !== '.' ? parseFloat(next) : undefined
          }
        });
      }}
    />
  );
});

const FieldLabel = ({ label, tooltip, badge, color }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, minHeight: 24 }}>
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#555' }}>{label}</Typography>
    {tooltip && (
      <Tooltip title={tooltip}>
        <InfoOutlinedIcon sx={{ fontSize: 14, color: '#9e9e9e', cursor: 'help' }} />
      </Tooltip>
    )}
    {badge && (
      <Chip
        label={badge}
        size="small"
        sx={{
          height: 18,
          fontSize: 11,
          fontWeight: 700,
          color: '#fff',
          backgroundColor: color,
          '& .MuiChip-label': { px: 0.75 }
        }}
      />
    )}
  </Box>
);

const RateField = ({ name, value, onChange, color, prefix, decimalScale, maxIntegerDigits, allowNegative }) => {
  const scale = decimalScale ?? 3;
  const intDigits = maxIntegerDigits ?? PRIME_MAX_INTEGER_DIGITS;
  return (
    <TextField
      variant="standard"
      name={name}
      value={value}
      onChange={onChange}
      fullWidth
      slotProps={{
        input: {
          inputComponent: RateNumberFormat,
          endAdornment: (
            <Typography component="span" sx={{ color, fontWeight: 700, fontSize: '1.1rem', ml: 0.5 }}>
              %
            </Typography>
          ),
          inputProps: {
            name,
            allowNegative: !!allowNegative,
            prefix: prefix || '',
            decimalScale: scale,
            maxIntegerDigits: intDigits,
            isAllowed: values => {
              const { formattedValue, value: raw } = values;
              if (formattedValue === '' || formattedValue === '-' || formattedValue === '+') return true;
              const next = autoInsertRateDecimal(raw, scale, intDigits);
              const floatValue = parseFloat(next);
              return Number.isFinite(floatValue) && floatValue >= -20 && floatValue <= 20;
            }
          }
        }
      }}
      sx={{
        '& .MuiInput-input': {
          color: `${color} !important`,
          fontWeight: 700,
          fontSize: '1.35rem',
          textAlign: 'left',
          padding: '2px 0'
        },
        '& .MuiInput-underline:before': { borderBottomColor: `${color} !important` },
        '& .MuiInput-underline:after': { borderBottomColor: `${color} !important` },
        '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: `${color} !important` },
        '& .MuiInputBase-adornedEnd > *': { color: `${color} !important` }
      }}
    />
  );
};

export const getEffectiveLoanInterestRates = ({ hiborBase, hiborAddOn, primeBase, primeSubtract }) => {
  const hibor = (Number(hiborBase) || 0) + (Number(hiborAddOn) || 0);
  const prime = (Number(primeBase) || 0) - (Number(primeSubtract) || 0);
  const lowerIsHibor = hibor <= prime;
  return {
    hiborRate: hibor,
    primeRate: prime,
    lowerRate: lowerIsHibor ? hibor : prime,
    lowerIsHibor,
    lowerIsPrime: !lowerIsHibor
  };
};

const LoanInterestCalculatorDialog = props => {
  const {
    intl,
    open,
    hiborBase,
    hiborAddOn,
    primeBase,
    primeSubtract,
    onChange,
    onClose,
    onSubmit,
    ...rest
  } = props;

  // undefined = never set → default; null = user cleared → show empty (counts as 0)
  const formattedHiborBase = hiborBase !== undefined ? hiborBase : DEFAULT_PREMIUM_FINANCING_PROFILE.hiborBase;
  const formattedHiborAddOn = hiborAddOn !== undefined ? hiborAddOn : DEFAULT_PREMIUM_FINANCING_PROFILE.hiborAddOn;
  const formattedPrimeBase = primeBase !== undefined ? primeBase : DEFAULT_PREMIUM_FINANCING_PROFILE.primeBase;
  const formattedPrimeSubtract =
    primeSubtract !== undefined ? primeSubtract : DEFAULT_PREMIUM_FINANCING_PROFILE.primeSubtract;

  const { hiborRate, primeRate, lowerIsHibor, lowerIsPrime } = getEffectiveLoanInterestRates({
    hiborBase: formattedHiborBase,
    hiborAddOn: formattedHiborAddOn,
    primeBase: formattedPrimeBase,
    primeSubtract: formattedPrimeSubtract
  });

  const handleFieldChange = event => {
    onChange({
      target: {
        name: event.target.name,
        value: event.target.value ?? null
      }
    });
  };

  const pasteRate = rate => {
    onSubmit(sanitizeLoanInterest(rate));
    onClose();
  };

  const lowerBadge = intl.formatMessage({ id: 'pfa-loan-interest-lower' });
  const pasteLabel = intl.formatMessage({ id: 'paste-use' });
  const pasteBtnSx = { flexShrink: 0, p: '2px', m: 0 };

  return (
    <Dialog
      className="loan-interest-calculator-dialog"
      maxWidth="xs"
      fullWidth
      open={open}
      onClose={onClose}
      {...rest}
      slots={{
        transition: SlideTransition
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
          <Typography component="span" sx={{ fontSize: 18, fontWeight: 700, color: '#444' }}>
            {intl.formatMessage({ id: 'pfa-loan-interest-cal-dialog-title' })}
          </Typography>
          <Tooltip title={intl.formatMessage({ id: 'pfa-loan-interest-cal-dialog-hint' })}>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: '#9e9e9e', cursor: 'help' }} />
          </Tooltip>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2.5}>
          <Grid size={12}>
            <FieldLabel
              label={intl.formatMessage({ id: 'pfa-hibor-base' })}
              badge={lowerIsHibor ? lowerBadge : null}
              color={INPUT_COLOR}
            />
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <RateField
                  name="hiborBase"
                  value={formattedHiborBase ?? ''}
                  onChange={handleFieldChange}
                  color={INPUT_COLOR}
                  decimalScale={2}
                  maxIntegerDigits={HIBOR_MAX_INTEGER_DIGITS}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <RateField
                  name="hiborAddOn"
                  value={formattedHiborAddOn ?? ''}
                  onChange={handleFieldChange}
                  color={INPUT_COLOR}
                  prefix="+ "
                  decimalScale={2}
                  maxIntegerDigits={HIBOR_MAX_INTEGER_DIGITS}
                />
              </Box>
              <Button
                color="primary"
                variant={lowerIsHibor ? 'contained' : 'text'}
                onClick={() => pasteRate(hiborRate)}
                sx={pasteBtnSx}
              >
                {pasteLabel}
              </Button>
            </Box>
          </Grid>

          <Grid size={12}>
            <FieldLabel
              label={intl.formatMessage({ id: 'pfa-prime-base' })}
              badge={lowerIsPrime ? lowerBadge : null}
              color={INPUT_COLOR}
            />
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <RateField
                  name="primeBase"
                  value={formattedPrimeBase ?? ''}
                  onChange={handleFieldChange}
                  color={INPUT_COLOR}
                  decimalScale={3}
                  maxIntegerDigits={PRIME_MAX_INTEGER_DIGITS}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <RateField
                  name="primeSubtract"
                  value={
                    formattedPrimeSubtract == null
                      ? ''
                      : formattedPrimeSubtract === 0
                        ? 0
                        : -Math.abs(formattedPrimeSubtract)
                  }
                  onChange={event => {
                    const raw = event.target.value;
                    onChange({
                      target: {
                        name: 'primeSubtract',
                        value: raw == null ? null : Math.abs(raw)
                      }
                    });
                  }}
                  color={INPUT_COLOR}
                  decimalScale={2}
                  maxIntegerDigits={PRIME_MAX_INTEGER_DIGITS}
                  allowNegative
                />
              </Box>
              <Button
                color="primary"
                variant={lowerIsPrime ? 'contained' : 'text'}
                onClick={() => pasteRate(primeRate)}
                sx={pasteBtnSx}
              >
                {pasteLabel}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {intl.formatMessage({ id: 'Cancel' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default injectIntl(LoanInterestCalculatorDialog);
